/**
 * The alerts that hold now (phase 7b item 4): severity, what, where, since
 * when, and whether people were told. `alerts`, `loading` and `error` are
 * independent props (docs/testing/components.md). `available: false` is a
 * backend that keeps no alerts (a node): said, not shown as an error.
 */
import { RefreshCwIcon, SettingsIcon } from 'lucide-react';
import type { AlertWire } from '@norna/api-contract/alerts.js';
import { alertLabel, severityTone } from '../../lib/alerts.js';
import { Badge } from '../ui/badge.js';
import { Button } from '../ui/button.js';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card.js';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table.js';

export type AlertListProps = {
  readonly available: boolean;
  readonly alerts: readonly AlertWire[];
  readonly loading: boolean;
  readonly error: string | null;
  /** Offer the settings (a tenant admin). */
  readonly canConfigure: boolean;
  readonly onRetry: () => void;
  readonly onConfigure: () => void;
};

function when(iso: string): string {
  return iso.replace('T', ' ').slice(0, 19);
}

export function AlertList({ available, alerts, loading, error, canConfigure, onRetry, onConfigure }: AlertListProps) {
  return (
    <Card data-testid="alert-list">
      <CardHeader className="flex flex-row items-center justify-between gap-4">
        <div className="grid gap-1.5">
          <CardTitle>Active alerts</CardTitle>
          <CardDescription>What needs attention now. Raised and resolved automatically; see the history below.</CardDescription>
        </div>
        {canConfigure ? (
          <Button variant="outline" onClick={onConfigure} data-testid="alert-settings">
            <SettingsIcon />
            Notification settings
          </Button>
        ) : null}
      </CardHeader>
      <CardContent>
        {!available ? (
          <p className="py-8 text-center text-sm text-muted-foreground" data-testid="alerts-unavailable">
            Alerts are kept by the cloud. Open the cloud portal to see them.
          </p>
        ) : null}
        {available && error !== null ? (
          <div role="alert" className="flex items-center justify-between gap-4 rounded-md border border-destructive p-4 text-sm">
            <span className="text-destructive">Could not load alerts: {error}</span>
            <Button variant="outline" size="sm" onClick={onRetry}>
              <RefreshCwIcon />
              Retry
            </Button>
          </div>
        ) : null}
        {available && loading && alerts.length === 0 ? <p className="py-8 text-center text-sm text-muted-foreground">Loading alerts…</p> : null}
        {available && !loading && error === null && alerts.length === 0 ? (
          <p className="py-8 text-center text-sm text-muted-foreground" data-testid="alerts-empty">
            All clear: nothing needs attention.
          </p>
        ) : null}
        {alerts.length > 0 ? (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-28">Severity</TableHead>
                <TableHead>Alert</TableHead>
                <TableHead>Site</TableHead>
                <TableHead className="w-44">Since</TableHead>
                <TableHead className="w-24">Notified</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {alerts.map((a) => (
                <TableRow key={a.key} data-testid="alert-row" data-alert-key={a.key}>
                  <TableCell>
                    <Badge variant={severityTone(a.severity)}>{a.severity}</Badge>
                  </TableCell>
                  <TableCell>
                    <div className="font-medium">{alertLabel(a.type)}</div>
                    <div className="text-sm text-muted-foreground">{a.summary}</div>
                    {a.acknowledged === null ? null : (
                      <div className="text-xs text-muted-foreground" data-testid="alert-acknowledged">
                        Acknowledged by {a.acknowledged.by} at {when(a.acknowledged.at)}
                      </div>
                    )}
                  </TableCell>
                  <TableCell className="text-sm">
                    {a.siteId}
                    {a.studioId === null ? '' : ` · ${a.studioId}`}
                  </TableCell>
                  <TableCell className="text-sm tabular-nums">{when(a.status === 'active' ? a.raisedAt : (a.resolvedAt ?? a.raisedAt))}</TableCell>
                  <TableCell className="text-sm">{a.notifiedAt === null ? '—' : 'yes'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : null}
      </CardContent>
    </Card>
  );
}
